import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IFormValue, TErrors } from 'FormContext';
import useValidation from '../useValidation';
import shallowEqual from '../utils/shallowEqual';
import * as listFormData from '../utils/listFormData';
import connectSectionToContext from './connectSectionToContext';

export interface IListFormSectionChildProps<D>
  extends Pick<IFormValue, 'registerValidators' | 'unregisterValidators' | 'onChange'> {
  /** Adds a new entry to the entry array */
  addEntry(newEntry: D): void;
  /** Data entries for the list form section */
  entries: {
    data: D;
    errors: string[] | undefined;
    index: number;
    key: string;
    name: string;
  }[];
  /** The length of the list form section */
  length: number;
  /** A map of section names to the index number in the entry array */
  nameToIndexMap: Record<string, number>;
  /** Removes an entry from the entry array */
  removeEntry(name: string): void;
}

export interface IListFormSectionProps<D>
  extends Partial<
    Pick<IFormValue, 'registerSelfValidatingChild' | 'unregisterSelfValidatingChild' | 'onChange'>
  > {
  /** The current data of the list form section */
  data?: D[];
  /** Errors with the data of the list form section */
  errors?: Record<keyof D, string>[];
  /** A function that returns the children of the list form section. Used if no component is passed. */
  children?(props: IListFormSectionChildProps<D>): JSX.Element[] | JSX.Element;
  /** A child component to display child the list from children.  */
  component?: ComponentType<IListFormSectionChildProps<D>>;
  /** The initial set of errors with the data */
  initialErrors?: TErrors[];
  /** The name of the list form section */
  name: string;
}

function ListFormSection<D = any>(props: IListFormSectionProps<D>) {
  const propsRef = useRef<IListFormSectionProps<D>>();
  /* This is some black magic I discovered to fix some serious performance issues.
   *
   * This `propsRef` value is equivalent to `this.props` in a class component. If I reference
   * `props` directly in a `useCallback` function, it will get the version of props that was present
   * when the callback was created by closure. A ref on the other hand, using the `current`
   * property, has its value derived at runtime, meaning you don't have to closure scope the props.
   * This allows `addEntry` and `removeEntry` to have no deps, meaning they will never change.
   * `addEntry` and `removeEntry` were causing all entries passed to the children of
   * `ListFormSection` to change on each keypress, meaning each child had to rerender.
   *
   * In order for these performance enhancements to work, the component rendered for each entry
   * must be memoized. Otherwise when the ListFormSection render prop is called it will create a
   * new tree and render all children. See the nested list form section example for a template.
   */
  propsRef.current = props;
  const {
    // these variables are not used directly but kept here to keep them out of `rest`
    children,
    component: Component,
    data = [],
    errors = [],
    initialErrors,
    name,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onChange: _onChange,
    registerSelfValidatingChild,
    unregisterSelfValidatingChild,
    ...rest
  } = props;
  const [nameToIndexMap, setIndexMap] = useState(() =>
    listFormData.createNameToIndexMap(name, data)
  );

  useEffect(() => {
    setIndexMap((prev) => listFormData.patchNameToIndexMap(name, prev, data));
  }, [data, name]);

  const onChange = useCallback(
    (_changedName: any, value: { [key: string]: D }) => {
      const {
        data: parentData = {},
        name: parentName,
        onChange: parentOnChange,
      } = propsRef.current!;
      const nextData = listFormData.mapPrivateDataToPublic(nameToIndexMap, value);

      if (shallowEqual(nextData, parentData)) {
        return;
      }

      parentOnChange!(parentName, nextData);
    },
    [nameToIndexMap]
  );

  const addEntry = useCallback((initialValue: D) => {
    const { data: parentData, name: parentName, onChange: parentOnChange } = propsRef.current!;
    parentOnChange!(
      parentName,
      Array.isArray(parentData) ? [...parentData, initialValue] : [initialValue]
    );
  }, []);

  const removeEntry = useCallback((nameToRemove: string) => {
    setIndexMap((prev) => {
      const {
        data: parentData = [],
        name: parentName,
        onChange: parentOnChange,
      } = propsRef.current!;
      const indexForName = prev[nameToRemove];
      const nextMap = listFormData.fromEntries(
        Object.entries(prev).filter(([current]) => current !== nameToRemove)
      );

      parentOnChange!(
        parentName,
        parentData.filter((value, index) => index !== indexForName)
      );

      return nextMap;
    });
  }, []);

  const [ChildProvider, childContext, { validate }] = useValidation({
    data: useMemo(
      () => listFormData.mapPublicDataToPrivate(nameToIndexMap, data),
      [nameToIndexMap, data]
    ),
    initialErrors: useMemo(
      () => listFormData.mapPublicDataToPrivate(nameToIndexMap, errors || initialErrors),
      [nameToIndexMap, errors, initialErrors]
    ),
    name,
    onChange,
  });

  useEffect(() => {
    registerSelfValidatingChild!(name, () =>
      validate().then(({ data: nextData, errors: nextErrors, isValid }) => ({
        data: listFormData.mapPrivateDataToPublic(nameToIndexMap, nextData),
        errors: listFormData.mapPrivateDataToPublic(nameToIndexMap, nextErrors),
        isValid,
      }))
    );

    return () => unregisterSelfValidatingChild!(name);
  }, [validate, name, nameToIndexMap, registerSelfValidatingChild, unregisterSelfValidatingChild]);

  const sortedEntries = useMemo(
    () => Object.entries(nameToIndexMap).sort(listFormData.sortByIndex),
    [nameToIndexMap]
  );
  const { length } = sortedEntries;
  const childProps = {
    ...rest,
    addEntry,
    entries: useMemo(
      () =>
        sortedEntries.map(([entryName, index]) => ({
          data: childContext.data[entryName] as D,
          errors: childContext.errors?.[entryName],
          index,
          key: entryName,
          name: entryName,
        })),
      [childContext.data, childContext.errors, sortedEntries]
    ),
    length,
    nameToIndexMap,
    onChange: childContext.onChange,
    registerValidators: childContext.registerValidators,
    removeEntry,
    unregisterValidators: childContext.unregisterValidators,
  };

  return (
    <ChildProvider value={childContext}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {Component ? <Component {...childProps} /> : children?.(childProps)}
    </ChildProvider>
  );
}

export default connectSectionToContext(ListFormSection);
